import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

function ScheduledExports() {
  const [exports, setExports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newExport, setNewExport] = useState({
    export_type: 'budget',
    frequency: 'weekly',
    day_of_week: 0,
    day_of_month: 1,
    hour: 9,
  });

  const loadExports = async () => {
    try {
      const response = await fetch(`${API}/exports/scheduled`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setExports(data.scheduled_exports || []);
      }
    } catch (error) {
      console.error('Error loading exports:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadExports();
  }, []);

  const handleCreateExport = async () => {
    setSaving(true);
    try {
      const payload = {
        export_type: newExport.export_type,
        frequency: newExport.frequency,
        hour: newExport.hour,
      };
      
      if (newExport.frequency === 'weekly') {
        payload.day_of_week = newExport.day_of_week;
      } else {
        payload.day_of_month = newExport.day_of_month;
      }
      
      const response = await fetch(`${API}/exports/scheduled`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      
      if (response.ok) {
        setShowAddForm(false);
        setNewExport({
          export_type: 'budget',
          frequency: 'weekly',
          day_of_week: 0,
          day_of_month: 1,
          hour: 9,
        });
        loadExports();
      } else {
        const data = await response.json();
        alert(data.detail || 'Failed to create export');
      }
    } catch (error) {
      console.error('Error creating export:', error);
      alert('Failed to create export');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleExport = async (exportId, currentActive) => {
    try {
      const response = await fetch(`${API}/exports/scheduled/${exportId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ is_active: !currentActive }),
      });
      
      if (response.ok) {
        loadExports();
      }
    } catch (error) {
      console.error('Error toggling export:', error);
    }
  };

  const handleDeleteExport = async (exportId) => {
    if (!window.confirm('Delete this scheduled export?')) return;
    
    try {
      const response = await fetch(`${API}/exports/scheduled/${exportId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      
      if (response.ok) {
        loadExports();
      }
    } catch (error) {
      console.error('Error deleting export:', error);
    }
  };

  const getScheduleText = (exp) => {
    const hourText = exp.hour === 0 ? '12 AM' : exp.hour < 12 ? `${exp.hour} AM` : exp.hour === 12 ? '12 PM' : `${exp.hour - 12} PM`;
    
    if (exp.frequency === 'weekly') {
      return `Every ${DAYS_OF_WEEK[exp.day_of_week]} at ${hourText}`;
    } else {
      const day = exp.day_of_month;
      const suffix = day === 1 ? 'st' : day === 2 ? 'nd' : day === 3 ? 'rd' : 'th';
      return `Monthly on the ${day}${suffix} at ${hourText}`;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <Link to="/dashboard" className="text-gray-500 hover:text-gray-700 text-sm">
          ← Back to Dashboard
        </Link>
        <h1 className="text-3xl font-bold text-gray-900 mt-2" data-testid="scheduled-exports-title">
          📧 Scheduled Exports
        </h1>
        <p className="text-gray-600">Automatically receive budget or debt reports via email</p>
      </div>

      {/* Add New Export Button */}
      {!showAddForm && (
        <button
          onClick={() => setShowAddForm(true)}
          className="w-full py-4 border-2 border-dashed border-gray-300 rounded-2xl text-gray-500 hover:border-blue-400 hover:text-blue-600 transition-colors"
          data-testid="add-export-btn"
        >
          + Schedule New Export
        </button>
      )}

      {/* Add Form */}
      {showAddForm && (
        <div className="bg-white rounded-2xl border border-gray-200 p-6 space-y-4">
          <h2 className="text-lg font-bold text-gray-900">Create Scheduled Export</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Export Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Export Type</label>
              <select
                value={newExport.export_type}
                onChange={(e) => setNewExport({ ...newExport, export_type: e.target.value })}
                className="w-full px-4 py-2 border border-gray-200 rounded-xl"
              >
                <option value="budget">Budget Summary</option>
                <option value="debt">Debt Payoff Plan</option>
              </select>
            </div>
            
            {/* Frequency */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Frequency</label>
              <select
                value={newExport.frequency}
                onChange={(e) => setNewExport({ ...newExport, frequency: e.target.value })}
                className="w-full px-4 py-2 border border-gray-200 rounded-xl"
              >
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
            
            {/* Day Selection */}
            {newExport.frequency === 'weekly' ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Day of Week</label>
                <select
                  value={newExport.day_of_week}
                  onChange={(e) => setNewExport({ ...newExport, day_of_week: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 border border-gray-200 rounded-xl"
                >
                  {DAYS_OF_WEEK.map((day, i) => (
                    <option key={i} value={i}>{day}</option>
                  ))}
                </select>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Day of Month</label>
                <select
                  value={newExport.day_of_month}
                  onChange={(e) => setNewExport({ ...newExport, day_of_month: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 border border-gray-200 rounded-xl"
                >
                  {Array.from({ length: 28 }, (_, i) => i + 1).map((day) => (
                    <option key={day} value={day}>{day}</option>
                  ))}
                </select>
              </div>
            )}
            
            {/* Hour */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Time (Hour)</label>
              <select
                value={newExport.hour}
                onChange={(e) => setNewExport({ ...newExport, hour: parseInt(e.target.value) })}
                className="w-full px-4 py-2 border border-gray-200 rounded-xl"
              >
                {Array.from({ length: 24 }, (_, i) => i).map((hour) => (
                  <option key={hour} value={hour}>
                    {hour === 0 ? '12 AM' : hour < 12 ? `${hour} AM` : hour === 12 ? '12 PM' : `${hour - 12} PM`}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="flex gap-3">
            <button
              onClick={handleCreateExport}
              disabled={saving}
              className="px-6 py-2 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Creating...' : 'Create Export'}
            </button>
            <button
              onClick={() => setShowAddForm(false)}
              className="px-6 py-2 text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Exports List */}
      {exports.length > 0 ? (
        <div className="space-y-4">
          <h2 className="text-xl font-bold text-gray-900">Your Scheduled Exports</h2>
          
          {exports.map((exp) => (
            <div 
              key={exp.id} 
              className={`bg-white rounded-2xl border p-6 ${exp.is_active ? 'border-gray-200' : 'border-gray-200 opacity-60'}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`text-3xl ${exp.export_type === 'budget' ? '' : ''}`}>
                    {exp.export_type === 'budget' ? '📊' : '💳'}
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900">
                      {exp.export_type === 'budget' ? 'Budget Summary' : 'Debt Payoff Plan'}
                    </h3>
                    <p className="text-sm text-gray-600">{getScheduleText(exp)}</p>
                    {exp.last_sent && (
                      <p className="text-xs text-gray-400">
                        Last sent: {new Date(exp.last_sent).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  {/* Toggle */}
                  <button
                    onClick={() => handleToggleExport(exp.id, exp.is_active)}
                    className={`relative w-14 h-8 rounded-full transition-colors ${
                      exp.is_active ? 'bg-green-500' : 'bg-gray-300'
                    }`}
                  >
                    <div className={`absolute w-6 h-6 bg-white rounded-full top-1 transition-transform shadow ${
                      exp.is_active ? 'translate-x-7' : 'translate-x-1'
                    }`} />
                  </button>
                  
                  {/* Delete */}
                  <button
                    onClick={() => handleDeleteExport(exp.id)}
                    className="text-gray-400 hover:text-red-500 transition-colors"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : !showAddForm && (
        <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
          <div className="text-5xl mb-4">📧</div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">No Scheduled Exports</h2>
          <p className="text-gray-600 mb-4">
            Set up automatic email exports of your budget or debt payoff reports
          </p>
        </div>
      )}

      {/* Info Card */}
      <div className="bg-blue-50 rounded-2xl p-6 border border-blue-200">
        <h3 className="font-bold text-blue-900 mb-2">📬 How It Works</h3>
        <ul className="text-blue-800 text-sm space-y-1">
          <li>• Excel files are automatically generated and emailed to your registered email</li>
          <li>• Budget exports include bills, income, and pay period breakdowns</li>
          <li>• Debt exports include your full payoff plan and progress</li>
          <li>• You can pause or resume exports anytime</li>
        </ul>
      </div>
    </div>
  );
}

export default ScheduledExports;
